import { useApprovingInvoiceReportsSearch } from '@/context/services/approving_invoice_reports';
import { useExportApprovingInvoiceReports } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import {
  ApprovingInvoiceReportsSearchQuery,
  FetchApprovingInvoiceReportsArgs,
} from '@/context/services/approving_invoice_reports/type';
import { useDownload } from '@/hooks/useDownload';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  ModernPaginationProps,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import { SearchApprovingInvoiceReportsCsvEncode } from 'ap-openapi';
import { useCallback, useEffect, useState } from 'react';

export const useApprovingInvoiceReportsSearchData = (
  query: FetchApprovingInvoiceReportsArgs,
  setError: (error: Error) => void
) => {
  const {
    data,
    pagination: _pagination,
    refetch,
    isError,
    error,
    isFetching,
  } = useApprovingInvoiceReportsSearch(query);

  const [isInitialError, setIsInitialError] = useState(true);
  const notify = useToastAPI();
  const TOAST_NOTICE_MESSAGE_KEY = 'toast-notice-message';

  const handleError = useCallback(() => {
    const errorMessage =
      isCommonError(error ?? {}) && Array.isArray(error?.messages)
        ? error?.messages.join('')
        : String(error?.messages);
    if (errorMessage !== null && errorMessage !== 'undefined') {
      setError({
        name: 'ApprovingInvoiceReportsTableError',
        message: String(errorMessage),
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isInitialError && isError && !isFetching) {
      setIsInitialError(false);
      handleError();
      return;
    }
    if (isError && !isFetching) {
      handleError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isInitialError]);

  useEffect(() => {
    const toastNoticeMessage = sessionStorage.getItem(TOAST_NOTICE_MESSAGE_KEY);
    if (toastNoticeMessage) {
      notify.success({
        title: toastNoticeMessage,
        duration: 3,
      });
      sessionStorage.removeItem(TOAST_NOTICE_MESSAGE_KEY);
    }
  }, [notify]);

  return {
    data,
    pagination: _pagination,
    refetch,
    isFetching,
  };
};

export const useExportApprovingInvoiceReportsQuery = (
  query: FetchApprovingInvoiceReportsArgs
) => {
  const { t } = useTranslation();
  const download = useDownload();
  const notify = useToastAPI();
  const [csvEncode, setCsvEncode] = useState<
    { encodeType: SearchApprovingInvoiceReportsCsvEncode } | undefined
  >(undefined);

  const { data, isLoading, refetch, status } = useExportApprovingInvoiceReports(
    {
      ...query,
      csv_encode: csvEncode?.encodeType,
    }
  );

  const handleExport = useCallback(
    (csv_encode: SearchApprovingInvoiceReportsCsvEncode) => () => {
      setCsvEncode({ encodeType: csv_encode });
    },
    [setCsvEncode]
  );

  useEffect(() => {
    if (csvEncode !== undefined) {
      refetch();
    }
  }, [csvEncode, refetch, setCsvEncode]);

  // Handle successful download
  useEffect(() => {
    if (!data) return;
    download({
      value: data,
      fileName: `${t('approving_invoice_reports.export.filename')}.csv`,
    });
  }, [data, download, t]);

  // Handle 400 error notification
  useEffect(() => {
    if (status === 400) {
      notify.error({
        title: t('approving_invoice_reports.export_csv.error_message'),
        duration: 3,
      });
    }
  }, [status, notify, t]);

  return { data, isLoading, handleExport };
};

type useReportActionsProps = (props: {
  pagination: ModernPaginationProps;
  onSearch: (value: ApprovingInvoiceReportsSearchQuery) => void;
  onDrawerClose: () => void;
}) => {
  onActionFailed: () => void;
};

export const useReportActions: useReportActionsProps = ({
  pagination,
  onSearch,
  onDrawerClose,
}) => {
  const onActionFailed = () => {
    if (pagination.currentPage !== 1) {
      onSearch({ page: 1 });
      return;
    }
    onDrawerClose();
    onSearch({ page: pagination.currentPage });
  };

  return {
    onActionFailed,
  };
};
